import { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";

import LinkedSuccessAuth from "@/components/onboarding/LinkedSuccessAuth";
import PhoneFirstAuth from "@/components/onboarding/PhoneFirstAuth";
import RouteLoadingShell from "@/components/shared/RouteLoadingShell";
import { getPreferencesBootstrap, preloadExistingPreferencesRoute } from "@/lib/api";
import { ONBOARDING_DESIGN_MODE } from "@/lib/onboarding-design-mode";
import {
  getPreferredSupabaseUser,
  isAuthenticatedSupabaseUser,
  readAuthReturnPathFromSearch,
  readOnboardingAuthDraft,
  withAuthReturnPath,
} from "@/lib/onboarding-auth";
import {
  clearPreferencesResumeTarget,
  getPreferencesLinkPath,
  readPreferencesResumeTargetFromSearch,
  resolvePreferencesAuthPath,
  resolvePreferencesEntryPath,
  shouldResumeToPreferences,
  syncPreferencesResumeTarget,
} from "@/lib/preferences-resume";
import { buildLinkedAuthPreferencesPath } from "@/lib/preferences-route";
import { getSupabaseBrowserClient } from "@/lib/supabase-browser-client";
import {
  readSupabaseAuthCallbackFromUrl,
  readSupabaseAuthErrorFromUrl,
  stripSupabaseAuthCallbackFromUrl,
  syncSupabaseSessionFromUrl,
} from "@/lib/supabase-session";
import { isCheckoutEnabledPlan, normalizeCheckoutPlan } from "@/lib/plans";
import { isMinAgentReturnPath } from "@/lib/mcp-onboarding";

function isLinkedContext(search = "") {
  return new URLSearchParams(search).get("context") === "linked";
}

function resolveAuthenticatedExistingPath(accountPayload, { shouldResume = false } = {}) {
  const resolvedEntryPath = resolvePreferencesEntryPath({
    hasAuthenticatedUser: true,
    accountPayload,
    forceLiveExistingPreferences: ONBOARDING_DESIGN_MODE,
  });
  if (shouldResume) {
    return resolvedEntryPath;
  }
  return resolvedEntryPath === getPreferencesLinkPath()
    ? "/link"
    : resolvedEntryPath;
}

function preloadRouteForPath(pathname = "") {
  if (String(pathname || "").startsWith("/setup")) {
    return import("@/pages/Setup");
  }
  if (String(pathname || "").startsWith("/link")) {
    return import("@/pages/Link");
  }
  return Promise.resolve();
}

function shouldBootstrapBeforeReturn(returnToPath = "") {
  const [pathname, rawSearch = ""] = String(returnToPath || "").split("?");
  const params = new URLSearchParams(rawSearch);
  return pathname === "/setup"
    && params.get("step") === "preferences"
    && params.get("mode") === "existing";
}

function readAccountIdFromReturn(returnToPath = "") {
  try {
    const url = new URL(String(returnToPath || ""), "https://min.cool");
    return String(url.searchParams.get("accountId") || "").trim();
  } catch {
    return "";
  }
}

async function prepareExistingPreferencesReturn(returnToPath = "") {
  const returnAccountId = readAccountIdFromReturn(returnToPath);
  const accountPayload = await getPreferencesBootstrap(returnAccountId, { forceFresh: true });
  const accountId = returnAccountId
    || String(accountPayload?.account?.id || "").trim()
    || String(accountPayload?.primaryAccountId || "").trim();
  await Promise.all([
    preloadExistingPreferencesRoute(accountId, {
      accountPayload,
    }),
    preloadRouteForPath(returnToPath),
  ]).catch(() => {
    // A warmup miss should not keep a proven signed-in user out of preferences.
  });
  clearPreferencesResumeTarget();
  return accountPayload;
}

export default function Auth() {
  const location = useLocation();
  const navigate = useNavigate();
  const linkedContext = isLinkedContext(location.search);
  const returnToPath = readAuthReturnPathFromSearch(location.search);
  const authEmailRedirectBasePath = linkedContext ? "/auth?context=linked" : "/auth";
  const authEmailRedirectPath = returnToPath
    ? withAuthReturnPath(authEmailRedirectBasePath, returnToPath)
    : authEmailRedirectBasePath;
  const [checkingAccount, setCheckingAccount] = useState(!linkedContext);
  const [lookupError, setLookupError] = useState("");
  const [showEmailAuth, setShowEmailAuth] = useState(() => isMinAgentReturnPath(returnToPath));
  const directPreferencesIntent = readPreferencesResumeTargetFromSearch(location.search) === "preferences";
  const callbackPreferencesIntent = typeof window !== "undefined"
    && Boolean(
      readSupabaseAuthCallbackFromUrl(window.location.href)
      || readSupabaseAuthErrorFromUrl(window.location.href),
    )
    && shouldResumeToPreferences(location.search);
  const preferencesIntent = directPreferencesIntent || callbackPreferencesIntent;
  const selectedPlanParam = new URLSearchParams(location.search).get("selectedPlan");
  const selectedPlan = isCheckoutEnabledPlan(selectedPlanParam)
    ? normalizeCheckoutPlan(selectedPlanParam, "fun")
    : "";
  const selectedPlanSuffix = selectedPlan ? `&selectedPlan=${encodeURIComponent(selectedPlan)}` : "";

  useEffect(() => {
    if (lookupError || isMinAgentReturnPath(returnToPath)) {
      setShowEmailAuth(true);
    }
  }, [lookupError, returnToPath]);

  useEffect(() => {
    if (linkedContext) {
      return undefined;
    }

    let cancelled = false;

    async function bootstrap() {
      try {
        if (!directPreferencesIntent && !callbackPreferencesIntent) {
          clearPreferencesResumeTarget();
        }

        const resumeTarget = syncPreferencesResumeTarget(location.search);
        const shouldResume = resumeTarget === "preferences";

        if (typeof window !== "undefined") {
          const authError = readSupabaseAuthErrorFromUrl(window.location.href);
          if (authError && !cancelled) {
            setLookupError(authError.message);
            const nextUrl = stripSupabaseAuthCallbackFromUrl(window.location.href);
            if (window.history?.replaceState) {
              window.history.replaceState(window.history.state, "", nextUrl);
            }
          }
        }

        const client = getSupabaseBrowserClient();
        if (!client) {
          return;
        }
        await syncSupabaseSessionFromUrl({ client });
        const {
          data: { session },
        } = await client.auth.getSession();
        if (!session?.access_token) {
          return;
        }

        const {
          data: userPayload,
          error: userError,
        } = await client.auth.getUser();
        if (userError && !session.user) {
          throw userError;
        }

        const user = getPreferredSupabaseUser({
          fetchedUser: userPayload?.user || null,
          sessionUser: session.user || null,
        });
        if (!isAuthenticatedSupabaseUser(user)) {
          return;
        }

        if (isMinAgentReturnPath(returnToPath)) {
          if (!cancelled) navigate(returnToPath, { replace: true });
          return;
        }

        const returnToUserMode = readOnboardingAuthDraft("signin").userMode;
        if (returnToPath && shouldBootstrapBeforeReturn(returnToPath)) {
          await prepareExistingPreferencesReturn(returnToPath);
          if (cancelled) {
            return;
          }
          navigate(returnToPath, { replace: true });
          return;
        }

        if (returnToPath && returnToUserMode === "existing") {
          navigate(returnToPath, { replace: true });
          return;
        }

        const accountPayload = await getPreferencesBootstrap("", { forceFresh: true });
        if (cancelled) {
          return;
        }
        if (returnToPath) {
          navigate(returnToPath, { replace: true });
          return;
        }

        const nextPath = resolveAuthenticatedExistingPath(accountPayload, {
          shouldResume,
        });
        if (nextPath.startsWith("/setup?step=preferences&mode=existing")) {
          clearPreferencesResumeTarget();
        }
        navigate(nextPath, { replace: true });
      } catch (error) {
        if (!cancelled) {
          const message = error instanceof Error ? error.message : "";
          if (/no linked account found/i.test(message)) {
            navigate(preferencesIntent ? getPreferencesLinkPath() : "/link", { replace: true });
            return;
          }
          if (message && !/authentication required/i.test(message)) {
            setLookupError(message);
          }
        }
      } finally {
        if (!cancelled) {
          setCheckingAccount(false);
        }
      }
    }

    void bootstrap();

    return () => {
      cancelled = true;
    };
  }, [callbackPreferencesIntent, directPreferencesIntent, linkedContext, location.search, navigate, preferencesIntent, returnToPath]);

  if (linkedContext) {
    return (
      <LinkedSuccessAuth
        search={location.search}
        emailRedirectPath={authEmailRedirectPath}
        onSkip={() => navigate(
          ONBOARDING_DESIGN_MODE
            ? "/setup?demoStep=preferences"
            : `/setup?step=preferences&mode=new${selectedPlanSuffix}`,
        )}
        onAuthenticated={({ accountId = "", setupMode = "" }) => navigate(
          returnToPath
            ? returnToPath
            : ONBOARDING_DESIGN_MODE
              ? "/setup?demoStep=preferences"
              : buildLinkedAuthPreferencesPath({
                setupMode,
                accountId,
                selectedPlan,
              }),
          { replace: Boolean(returnToPath) },
        )}
      />
    );
  }

  if (checkingAccount) {
    return (
      <RouteLoadingShell
        title="Checking your account…"
        copy="Seeing whether this browser already has a signed-in Min account."
      />
    );
  }

  const handleStandaloneAuthenticated = async ({ userMode, accountId = "", setupMode = "" }) => {
    if (isMinAgentReturnPath(returnToPath)) {
      navigate(returnToPath, { replace: true });
      return;
    }
    if (userMode !== "existing") {
      const nextPath = resolvePreferencesAuthPath(userMode, {
        forceLiveExistingPreferences: ONBOARDING_DESIGN_MODE,
      });
      navigate(nextPath, { replace: true });
      return;
    }

    try {
      if (returnToPath && shouldBootstrapBeforeReturn(returnToPath)) {
        await prepareExistingPreferencesReturn(returnToPath);
        navigate(returnToPath, { replace: true });
        return;
      }

      if (returnToPath) {
        navigate(returnToPath, { replace: true });
        return;
      }

      if (accountId && setupMode) {
        navigate(
          buildLinkedAuthPreferencesPath({
            setupMode,
            accountId,
            selectedPlan,
          }),
          { replace: true },
        );
        return;
      }

      const accountPayload = await getPreferencesBootstrap("", { forceFresh: true });
      const nextPath = resolveAuthenticatedExistingPath(accountPayload, {
        shouldResume: preferencesIntent,
      });
      if (nextPath.startsWith("/setup?step=preferences&mode=existing")) {
        await Promise.all([
          preloadExistingPreferencesRoute(String(accountPayload?.account?.id || ""), {
            accountPayload,
          }),
          preloadRouteForPath(nextPath),
        ]).catch(() => {
          // Keep the signed-in handoff moving even when warmup misses.
        });
        clearPreferencesResumeTarget();
      }
      navigate(nextPath, { replace: true });
    } catch (error) {
      const message = error instanceof Error ? error.message : "";
      if (/no linked account found/i.test(message)) {
        navigate(preferencesIntent ? getPreferencesLinkPath() : "/link", { replace: true });
        return;
      }
      const nextPath = resolvePreferencesAuthPath("existing", {
        forceLiveExistingPreferences: ONBOARDING_DESIGN_MODE,
      });
      navigate(nextPath, { replace: true });
    }
  };

  if (!showEmailAuth) {
    return (
      <PhoneFirstAuth
        errorMessage={lookupError}
        onSkip={() => navigate("/")}
        onUseEmail={() => setShowEmailAuth(true)}
        onNeedsLink={(path) => navigate(path || "/link", { replace: true })}
        onAuthenticated={(payload) => void handleStandaloneAuthenticated(payload)}
      />
    );
  }

  return (
    <>
      {lookupError ? (
        <div className="px-5 pt-4 text-center text-sm text-destructive">{lookupError}</div>
      ) : null}
      <LinkedSuccessAuth
        surface="signin"
        emailRedirectPath={authEmailRedirectPath}
        lockUserMode={false}
        onSkip={() => navigate("/")}
        onAuthenticated={(payload) => void handleStandaloneAuthenticated(payload)}
      />
    </>
  );
}
